import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { checkIfPromoProjection } from '@/feature/lobby/utils/lineupsUtils';
import { ProjectionType } from '@/types/api.generated';

const sortPromoPick = (projection: PlayerProjectionFragment) => {
    return !checkIfPromoProjection(projection.type) ? 1 : 0;
};

const sortByProjectionType = (projection1: PlayerProjectionFragment, projection2: PlayerProjectionFragment) => {
    return sortPromoPick(projection1) - sortPromoPick(projection2);
};

const sortByOrder = (projection1: PlayerProjectionFragment, projection2: PlayerProjectionFragment) => {
    return projection1.order - projection2.order;
};

export const sortProjections = (projection1: PlayerProjectionFragment, projection2: PlayerProjectionFragment) => {
    return sortByProjectionType(projection1, projection2) || sortByOrder(projection1, projection2);
};

/**
 * Groups projections in an object by their stat ( PASSING_YDS:[], RUSHING_YDS:[], etc.)
 * Within each group, projections are sorted by ascending value.
 * @param projections
 * @returns An object where the keys are the stats, and the values are arrays of projections for that stat sorted by ascending value.
 */
export const sortAndGroupProjectionsByStat = (
    projections: PlayerProjectionFragment[],
    playerProjectionFilter?: string
): { [key: string]: PlayerProjectionFragment[] } => {
    const grouped = projections.reduce((acc: { [key: string]: PlayerProjectionFragment[] }, currentObject) => {
        const statType = currentObject.key;
        if (acc[statType]) {
            acc[statType].push(currentObject);
        } else {
            acc[statType] = [currentObject];
        }
        return acc;
    }, {});

    //Make sure we are sorting for each stat, the markets by value or nonRegularValue based on the stat type
    Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => {
            const aValue = a.type === ProjectionType.Regular ? a.value : a.nonRegularValue || a.value;
            const bValue = b.type === ProjectionType.Regular ? b.value : b.nonRegularValue || b.value;
            return aValue - bValue;
        });
    });

    if (playerProjectionFilter && grouped[playerProjectionFilter]) {
        const { [playerProjectionFilter]: filteredGroup, ...rest } = grouped;
        return {
            [playerProjectionFilter]: filteredGroup,
            ...rest,
        };
    }

    return grouped;
};

/**
 * Sort function that will sort promo projections by importance.
 * Order will be Free Pick, Special, Nuke, Anchor.
 */
export const sortPromoProjections = (projection1: PlayerProjectionFragment, projection2: PlayerProjectionFragment) => {
    const order = [ProjectionType.FreePick, ProjectionType.Special, ProjectionType.Nuke, ProjectionType.Anchor];
    return order.indexOf(projection1.type) - order.indexOf(projection2.type);
};
